import * as React from 'react'
import {Fragment, Suspense, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {api} from '../api/api'
import '../css/modal.css'

const Dropzone = React.lazy(() => import('react-dropzone'))

interface ApplyData {
    title?: string
    name?: string
    email?: string
    phone?: string
    cv?: any
}

interface Data {
    title: string
    text: string
    brief: string[]
    requirements: string[]
}

interface Props {
    data: Data
    show: boolean
    action: () => void
    toggle: () => void
}

const Modal = (props: Props) => {
    useEffect(() => {
        const html = document.querySelector('html')
        if (props.show) {
            html?.classList.add('not-scrollable')
        } else {
            html?.classList.remove('not-scrollable')
        }
    }, [props.show])

    const applyData: ApplyData = {
        title: undefined,
        name: undefined,
        email: undefined,
        phone: undefined,
        cv: undefined
    }

    const tC = useTranslation('common').t
    const tM = useTranslation('modal').t

    const [state, setState] = useState({
        isApply: false,
        isDropdown: false,
        applyData
    })

    const renderData = () => (
        <div className="modal-upper-block">
            <div className="modal-header">
                <div className="title-40">{props.data.title}</div>
                <div className="text-14">{props.data.text}</div>
            </div>
            <div className="modal-body">
                <div className="title-18 accent-text">As the {props.data.title}, you will be:</div>
                <ul className="text-14">
                    {props.data.brief.map((point, i) => <li key={'ip-' + i}>{point}</li>)}
                </ul>
                <div className="title-18 accent-text">{tM('Required experience')}</div>
                <ul className="text-14">
                    {props.data.requirements.map((point, i) => <li key={'ip-' + i}>{point}</li>)}
                </ul>
            </div>
        </div>
    )

    const onDrop = (files) => setState({...state, applyData: {...state.applyData, cv: files[0]}})

    const renderImageDropzone = () => (
        <Suspense fallback={<div className="drag-n-drop" />}>
            <Dropzone onDrop={onDrop}>
                {({getRootProps, getInputProps}) => (
                    <div className="drag-n-drop" {...getRootProps()}>
                        <input {...getInputProps()} />
                        {state.applyData.cv ?
                            <div className="text-14">{state.applyData.cv.name}</div> :
                            <div className="text-14">
                                {tM('Drag & Drop your CV here or')} <a className="accent-text">{tM('browse')}</a>
                            </div>
                        }
                    </div>
                )}
            </Dropzone>
        </Suspense>
    )

    const setTitle = (title: string) => setState({...state, isDropdown: false, applyData: {title: title}})

    const submitApply = () => {
        const {title, name, email, phone, cv} = state.applyData
        if (name != undefined && email != undefined && phone != undefined && cv != undefined) {
            const payload = new FormData()
            payload.append('position', props.data.title)
            payload.append('name', title ? title + '. ' + name : name)
            payload.append('email', email)
            payload.append('phone', phone)
            payload.append('cv', cv)
            api.postApplicantCvData(payload)
                .then(response => {
                    if (response && response._id) {
                        props.toggle()
                        setTimeout(() => {
                            setState({
                                ...state,
                                isApply: false,
                                isDropdown: false,
                                applyData: {
                                    title: undefined
                                }
                            })
                            props.action()
                        }, 1000)
                    }
                })
                .catch(e => console.log(tM('CV Data post request is failed') + ': ' + e))
        }
    }

    const renderForm = () => (
        <Fragment>
            <div className="modal-upper-block">
                <div className="modal-body">
                    <div className="title-18 accent-text">Enter your credentials</div>
                    <div className="contact-input-row-block">
                        <div className={state.isDropdown ? 'dropdown is-active' : 'dropdown'}>
                            <div className="dropdown-button" onClick={() =>
                                setState({...state, isDropdown: !state.isDropdown})}>
                                <span>{state.applyData.title ? state.applyData.title : tM('Title')}</span>
                                <img src="https://static.persona.ly/images/dropdown-arrow.svg" />
                            </div>
                            <div className="dropdown-menu">
                                <a className="dropdown-item" onClick={() => setTitle('Mr')}>Mr</a>
                                <a className="dropdown-item" onClick={() => setTitle('Ms')}>Ms</a>
                            </div>
                        </div>
                        <div className="contact-input-container">
                            <input className="contact-input small-text" type="text" name="contactCompany" placeholder="&nbsp;"
                                   onInput={e => setState({
                                       ...state,
                                       applyData: {
                                           ...state.applyData,
                                           name: e.currentTarget.value
                                       }
                                   })} />
                            <span className="contact-input-label">{tM('Full name')}</span>
                        </div>
                    </div>
                    <div className="contact-input-row-block">
                        <div className="contact-input-container">
                            <input className="contact-input small-text" type="text" name="contactName" placeholder="&nbsp;"
                                   onInput={e => setState({
                                       ...state,
                                       applyData: {
                                           ...state.applyData,
                                           email: e.currentTarget.value
                                       }
                                   })} />
                            <span className="contact-input-label">Email address</span>
                        </div>
                        <div className="contact-input-container">
                            <input className="contact-input small-text" type="text" name="contactCompany" placeholder="&nbsp;"
                                   onInput={e => setState({
                                       ...state,
                                       applyData: {
                                           ...state.applyData,
                                           phone: e.currentTarget.value
                                       }
                                   })} />
                            <span className="contact-input-label">{tM('Phone')}</span>
                        </div>
                    </div>
                    {renderImageDropzone()}
                </div>
            </div>
        </Fragment>
    )

    return (
        <div className={'modal' + (props.show ? ' modal-show' : ' modal-hide')} >
            <div className="modal-card">
                <div className="close-modal-btn" onClick={() => {
                    props.toggle()
                    setTimeout(() => setState({
                        ...state,
                        isApply: false,
                        isDropdown: false,
                        applyData: {
                            title: undefined
                        }
                    }), 1000)
                }} />
                {state.isApply ? renderForm() : renderData()}
                <div className="modal-footer">
                    {state.isApply ?
                        <button className="btn-modal-continue" onClick={submitApply}>{tM('Submit Documents')}</button> :
                        <button className="btn-modal-continue" onClick={() => setState({...state, isApply: true})}>
                            {tC('Apply')}
                        </button>}
                </div>
            </div>
        </div>
    )
}

export default Modal


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/modal.tsx